'use client';

import { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';

const SERVICES = ["Strona WWW", "SEO", "Prospecting", "Pakiet Tożsamości", "Inne"];
const BUDGETS = ['< 5k', '5k-15k', '15k-50k', '50k+'];

export default function AssistantWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [isFooterInView, setIsFooterInView] = useState(false);
  
  // --- NOWOŚĆ: Stan widoczności (Cookies + Menu) ---
  const [canShowWidget, setCanShowWidget] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: [] as string[],
    budget: "",
    message: ""
  });

  // 1. Sprawdzanie zgody na cookies (Decyzja podjęta = Widget widoczny)
  useEffect(() => {
    const checkConsentDecision = () => {
      const consentMade = localStorage.getItem('cookie_consent');
      if (consentMade) {
        setCanShowWidget(true);
      }
    };
    checkConsentDecision();
    window.addEventListener('cookie_consent_updated', checkConsentDecision);
    return () => window.removeEventListener('cookie_consent_updated', checkConsentDecision);
  }, []);

  // 2. Nasłuchiwanie stanu Menu Mobilnego (żeby widget się ukrywał i nie robił "młyna")
  useEffect(() => {
    const handleMenuOpen = () => setIsMobileMenuOpen(true);
    const handleMenuClose = () => setIsMobileMenuOpen(false);

    window.addEventListener('mobile_menu_opened', handleMenuOpen);
    window.addEventListener('mobile_menu_closed', handleMenuClose);

    return () => {
      window.removeEventListener('mobile_menu_opened', handleMenuOpen);
      window.removeEventListener('mobile_menu_closed', handleMenuClose);
    };
  }, []);

  // 3. Wykrywanie stopki (aby nie zasłaniać treści na mobile)
  useEffect(() => {
    const footerElement = document.querySelector('footer');
    if (!footerElement) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFooterInView(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(footerElement);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const toggleService = (service: string) => {
    setFormData(prev => {
      const isSelected = prev.service.includes(service);
      if (isSelected) {
        return { ...prev, service: prev.service.filter(s => s !== service) };
      } else {
        return { ...prev, service: [...prev.service, service] };
      }
    });
  };

  const setBudget = (value: string) => {
    setFormData({ ...formData, budget: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");

    try {
      await addDoc(collection(db, "leads"), {
        ...formData,
        source: "widget",
        createdAt: serverTimestamp(),
        status: "new"
      });

      setStatus("success");
      setFormData({ name: "", email: "", phone: "", service: [], budget: "", message: "" });
      
    } catch (error) {
      console.error(error);
      setStatus("error");
    }
  };

  // JEŚLI NIE PODJĘTO DECYZJI O COOKIES -> NIE RENDERUJ WCALE
  if (!canShowWidget) return null;

  return (
    <div 
        className={`fixed bottom-6 right-6 z-[9999] flex flex-col items-end font-sans transition-all duration-500 ease-in-out animate-in fade-in zoom-in ${
            // UKRYWAMY JEŚLI: Stopka widoczna LUB Menu otwarte
            (isFooterInView || isMobileMenuOpen)
            ? 'opacity-0 pointer-events-none translate-y-10' 
            : 'opacity-100 translate-y-0'
        } ${
            // Przywracamy na desktopie (tylko jeśli powodem ukrycia była stopka, a nie menu)
            // (Jeśli menu jest otwarte - czyli mobile - to zawsze ukrywamy)
            (isFooterInView && !isMobileMenuOpen) ? 'lg:opacity-100 lg:pointer-events-auto lg:translate-y-0' : ''
        }`}
    >
      
      <AnimatePresence>
        {!isOpen && (
            <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="mb-3 mr-1 bg-white border border-gray-200 px-4 py-2 rounded-xl shadow-lg relative"
            >
                <span className="text-xs font-bold text-black whitespace-nowrap">
                    🔥 Darmowa wycena 24h
                </span>
                <div className="absolute -bottom-1.5 right-6 w-3 h-3 bg-white border-b border-r border-gray-200 transform rotate-45"></div>
            </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="mb-4 w-[350px] max-w-[calc(100vw-48px)] rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden flex flex-col max-h-[70vh]"
          >
            {/* HEADER */}
            <div className="bg-black p-4 text-white flex justify-between items-center shrink-0">
                <div>
                    <h3 className="font-bold text-sm">Asystent Webpol</h3>
                    <p className="text-[10px] text-gray-300">Odpowiadamy zazwyczaj w 24h</p>
                </div>
                <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white" aria-label="Zamknij czat">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
            </div>

            {/* BODY */}
            <div className="p-4 overflow-y-auto bg-gray-50 flex-1">
                {status === "success" ? (
                    <div className="h-full flex flex-col items-center justify-center text-center py-10">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-4">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
                        </div>
                        <h4 className="font-bold text-black">Wiadomość wysłana!</h4>
                        <p className="text-sm text-gray-800 mt-2 mb-6">Dziękujemy za kontakt. Odezwiemy się wkrótce.</p>
                        <button onClick={() => { setIsOpen(false); setStatus("idle"); }} className="text-xs font-bold text-blue-600 hover:underline">
                            Zamknij okno
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="bg-white p-3 rounded-xl rounded-tl-none border border-gray-200 shadow-sm text-xs font-medium text-black mb-4">
                            Cześć! 👋 Chcesz wycenić projekt bez dzwonienia? Wypełnij ten krótki formularz.
                        </div>
                        
                        <div className="space-y-3">
                            <input 
                                required type="text" name="name" placeholder="Imię i Nazwisko"
                                value={formData.name} onChange={handleChange}
                                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-black placeholder:text-gray-500 focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                            />
                            <input 
                                required type="email" name="email" placeholder="E-mail"
                                value={formData.email} onChange={handleChange}
                                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-black placeholder:text-gray-500 focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                            />
                            <input 
                                required type="tel" name="phone" placeholder="Telefon"
                                value={formData.phone} onChange={handleChange}
                                className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-black placeholder:text-gray-500 focus:outline-none focus:border-black focus:ring-1 focus:ring-black"
                            />
                        </div>

                        <div>
                            <p className="text-[10px] font-bold text-black uppercase tracking-wider mb-2">
                                Interesuje mnie (możesz zaznaczyć kilka):
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {SERVICES.map(s => {
                                    const isSelected = formData.service.includes(s);
                                    return (
                                        <button 
                                            type="button" 
                                            key={s} 
                                            onClick={() => toggleService(s)}
                                            className={`text-[10px] px-3 py-1.5 rounded-full border font-medium transition ${
                                                isSelected
                                                ? 'bg-black text-white border-black' 
                                                : 'bg-white text-black border-gray-300 hover:border-black'
                                            }`}
                                        >
                                            {s} {isSelected && '✓'}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            <p className="text-[10px] font-bold text-black uppercase tracking-wider mb-2">Budżet:</p>
                            <div className="flex flex-wrap gap-2">
                                {BUDGETS.map(b => (
                                    <button 
                                        type="button" 
                                        key={b} 
                                        onClick={() => setBudget(b)}
                                        className={`text-[10px] px-3 py-1.5 rounded-full border font-medium transition ${
                                            formData.budget === b 
                                            ? 'bg-blue-600 text-white border-blue-600' 
                                            : 'bg-white text-black border-gray-300 hover:border-black'
                                        }`}
                                    >
                                        {b}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <textarea 
                            name="message" rows={2} placeholder="Krótki opis projektu..."
                            value={formData.message} onChange={handleChange}
                            className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-black placeholder:text-gray-500 focus:outline-none focus:border-black focus:ring-1 focus:ring-black resize-none"
                        ></textarea>

                        <button 
                            type="submit" disabled={status === "submitting"}
                            className="w-full bg-black text-white font-bold py-3 rounded-lg hover:bg-gray-800 transition text-sm flex justify-center items-center shadow-lg"
                        >
                            {status === "submitting" ? "Wysyłanie..." : "Wyślij zapytanie"}
                        </button>
                    </form>
                )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? "Zamknij asystenta" : "Otwórz asystenta"}
        className={`flex h-14 w-14 items-center justify-center rounded-full shadow-xl transition-all hover:scale-110 focus:outline-none focus:ring-4 focus:ring-blue-300 ${isOpen ? 'bg-gray-200 text-gray-800 rotate-90' : 'bg-blue-600 text-white'}`}
      >
        {isOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
        )}
      </button>
    </div>
  );
}